import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { roundedDurationSelector } from '../ecg/selectors/ecg.selectors';
import { leadNamesSelector } from '../ecg/selectors/signal.selectors';
import { Column } from '../plot/contexts/column.context';
import { Plot } from '../plot/contexts/plot.context';
import { Row } from '../plot/contexts/row.context';
import { Strip } from '../plot/contexts/strip.context';
import * as navigationActions from '../navigation/actions/navigation.actions';
import * as stripActions from '../plot/actions/strip.actions';
import { PlotHeader } from '../plot/components/PlotHeader';
import { WaveTags } from '../plot/components/WaveTags';
import { BeatIntervals } from '../plot/components/BeatIntervals';
import { useQRSListener } from '../ecg/hooks/useQRSListener';
import { RestingLead } from './RestingLead';
import {
  getRestingDisplay,
  RESTING_STRIP_HEIGHT,
  RESTING_STRIP_WIDTH,
  RESTING_DURATION_ON_SCREEN,
  RESTING_ONSET_ON_SCREEN,
  RESTING_OFFSET_ON_SCREEN,
} from './resting.util';
import {
  displaySelector,
  gainSelector,
  restingLeadSelector,
} from '../layout/selectors/layout.selectors';
import { isToolActiveSelector } from '../tool/selectors/tool.selectors';
import { Tool } from '../tool/utils/tool.utils';
import { RULER_HEADER_HEIGHT } from '../ruler/components/RulerInfo';
import { Ruler2dContainer } from '../ruler2d/components/Ruler2dContainer';
import { Ruler2d } from '../ruler2d/components/Ruler2d';

export const RESTING_STRIP_ID = 'resting-plot';

export const RestingPlot: React.FC = () => {
  useQRSListener();
  const dispatch = useDispatch();
  const leads = useSelector(leadNamesSelector);
  const display = useSelector(displaySelector);
  const duration = useSelector(roundedDurationSelector);
  const leadChunks = getRestingDisplay(leads, display);
  const selectedLead = useSelector(restingLeadSelector);
  const isRuler2dActive = useSelector(isToolActiveSelector(Tool.ruler2d));
  const gain = useSelector(gainSelector);

  useEffect(() => {
    dispatch(navigationActions.go({ timestamp: RESTING_DURATION_ON_SCREEN }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      stripActions.update({ stripId: RESTING_STRIP_ID, strip: { gain } }),
    );
  }, [gain, dispatch]);

  return (
    <Strip
      id={RESTING_STRIP_ID}
      duration={RESTING_DURATION_ON_SCREEN}
      onset={RESTING_ONSET_ON_SCREEN}
      offset={RESTING_OFFSET_ON_SCREEN}
    >
      <Plot>
        <PlotHeader height={RULER_HEADER_HEIGHT}></PlotHeader>
        <PlotHeader>
          <WaveTags />
          <BeatIntervals />
        </PlotHeader>
      </Plot>
      <Plot
        data-testid='resting-leads'
        plotName='resting'
        style={{
          marginBottom: 1,
        }}
        cursor={isRuler2dActive ? 'crosshair' : undefined}
      >
        <Row height={(leadChunks[0].length + 1) * RESTING_STRIP_HEIGHT}>
          <Column width={RESTING_STRIP_WIDTH}>
            <Row height={leadChunks[0].length * RESTING_STRIP_HEIGHT}>
              {leadChunks.map((leadChunk, leadChunkIndex) => (
                <Column
                  key={leadChunkIndex}
                  width={RESTING_STRIP_WIDTH / leadChunks.length}
                >
                  {leadChunk.map((lead) => (
                    <RestingLead
                      key={lead}
                      leadChunkIndex={leadChunkIndex}
                      lead={lead}
                      duration={duration}
                      count={leadChunks.length}
                    />
                  ))}
                </Column>
              ))}
            </Row>
            <Row height={RESTING_STRIP_HEIGHT}>
              <Column width={RESTING_STRIP_WIDTH}>
                <RestingLead
                  leadChunkIndex={0}
                  lead={selectedLead}
                  duration={duration}
                  count={1}
                />
              </Column>
            </Row>
            <Ruler2dContainer>
              <Ruler2d />
            </Ruler2dContainer>
          </Column>
        </Row>
      </Plot>
    </Strip>
  );
};
